import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import Axios from "axios";
import {
  FaBus,
  FaUser,
  FaClock,
  FaMapMarkerAlt,
  FaRoute,
  FaPlus,
  FaUserTimes,
  FaEdit,
  FaTrashAlt,
  FaArrowLeft,
  FaExclamationTriangle,
  FaCalendarAlt,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import MdlAddServiceMonth from "../dialog/MdlAddServiceMonth";
import MdlEditServiceMonth from "../dialog/MdlEditServiceMonth";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import MyLoader from "../Spinner/MyLoader";
import myToast from "../Utils/MyToast";

const API_BASE = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SERVICE_ID = "SRV001";
const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getApplicableMonth(item) {
  const monthValue = item?.applicable_month || item?.service_month;
  if (monthValue && String(monthValue).includes("-")) {
    const [year, month] = String(monthValue).split("-");
    const monthIndex = parseInt(month, 10) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${MONTH_NAMES[monthIndex]} ${year}`;
    }
  }
  const now = new Date();
  return `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
}

function money(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : value ?? "0";
}

function canModifyMonth(item) {
  return (
    parseFloat(item?.amount_paid || 0) === 0 &&
    parseFloat(item?.amount_settled || 0) === 0
  );
}

const TransportStudentDetails = () => {
  const loggedIn = localStorage.getItem("token") !== null;

  const [loading, setLoading] = useState(false);
  const [isOpenAddServiceMonth, setIsOpenAddServiceMonth] = useState(false);
  const [isOpenEditServiceMonth, setIsOpenEditServiceMonth] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showDeleteMonthConfirm, setShowDeleteMonthConfirm] = useState(false);
  const [deletingMonth, setDeletingMonth] = useState(null);

  const [CLASS_ID, setClassId] = useState(null);
  const [SECTION_ID, setSectionId] = useState(null);
  const [SESSION_ID, setSessionId] = useState(null);
  const [StudentId, setStudentId] = useState(null);
  const [students, setStudents] = useState([]);
  const [mTransportFee, setTransportFee] = useState([]);

  const student = students[0] || null;
  const transport = student?.transport_info?.[0] || null;
  const classInfo = student?.class?.[0] || null;

  const hasCollectedFee = useMemo(
    () =>
      mTransportFee.some(
        (fee) =>
          parseFloat(fee.amount_paid) > 0 || parseFloat(fee.amount_settled) > 0
      ),
    [mTransportFee]
  );

  const classLabel = useMemo(() => {
    if (!classInfo) return "—";
    return [classInfo.class_name, classInfo.section_name]
      .filter(Boolean)
      .join(" · ");
  }, [classInfo]);

  const getStudentTransportDetails = useCallback(() => {
    if (!StudentId || !CLASS_ID || !SESSION_ID) return;

    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    Axios.get(`${API_BASE}getStudentTransportDetails.php`, {
      params: {
        school_code: SCHOOL_CODE,
        student_id: StudentId,
        class_id: CLASS_ID,
        session_id: SESSION_ID,
        service_id: SERVICE_ID,
      },
    })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          setTransportFee(response.data?.[0]?.transport_fee || []);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  }, [StudentId, CLASS_ID, SESSION_ID]);

  useEffect(() => {
    try {
      const TransportData =
        JSON.parse(localStorage.getItem("TransportData")) || {};
      if (!TransportData?.student_enrollment_no || !TransportData?.class?.[0]) {
        myToast.notify_warning("Student data missing. Open a student from the list.");
        return;
      }
      setStudentId(TransportData.student_enrollment_no);
      setClassId(TransportData.class[0].class_id);
      setSectionId(TransportData.class[0].section_id);
      setSessionId(TransportData.class[0].session_id);
      setStudents([TransportData]);
    } catch (error) {
      console.error(error);
      myToast.notify_error("Failed to load student details.");
    }
  }, []);

  useEffect(() => {
    if (students.length > 0) getStudentTransportDetails();
  }, [students, getStudentTransportDetails]);

  const handleCloseAddServiceMonth = useCallback(() => {
    setIsOpenAddServiceMonth((prev) => !prev);
  }, []);

  const handleCloseEditServiceMonth = useCallback(() => {
    setIsOpenEditServiceMonth((prev) => !prev);
  }, []);

  const AddServiceMonth = useCallback(() => {
    handleCloseAddServiceMonth();
  }, [handleCloseAddServiceMonth]);

  const handleAddServiceMonths = useCallback(
    async (selectedMonths) => {
      if (!selectedMonths?.length) {
        myToast.notify_warning("Please select at least one month to add.");
        return;
      }
      if (!StudentId || !CLASS_ID || !SECTION_ID || !SESSION_ID) {
        myToast.notify_error(
          "Required information is missing. Please refresh the page and try again."
        );
        return;
      }

      setLoading(true);
      try {
        const SCHOOL_CODE = SessionManager.getSchoolCode();
        const STAFF_ID = SessionManager.getStaffDetails()?.staff_id;
        if (!STAFF_ID || !SCHOOL_CODE) {
          myToast.notify_error(
            "Session information is missing. Please login again."
          );
          setLoading(false);
          return;
        }

        const params = new URLSearchParams({
          staff_id: STAFF_ID,
          transport_service_month: selectedMonths.join(","),
          student_id: StudentId,
          class_id: CLASS_ID,
          section_id: SECTION_ID,
          session_id: SESSION_ID,
          service_id: SERVICE_ID,
          school_code: SCHOOL_CODE,
        });

        const response = await Axios.get(
          `${API_BASE}AddTransportServiceMonth.php?${params.toString()}`
        );
        setLoading(false);

        if (response.data.api_status === "OK") {
          myToast.notify_success("Service month(s) added successfully!");
          getStudentTransportDetails();
        } else {
          myToast.notify_warning(
            response.data.api_remark ||
              "Failed to add transport service month(s)."
          );
        }
      } catch (error) {
        setLoading(false);
        console.error("Error adding transport service month(s):", error);
        myToast.notify_error("Failed to add transport service month(s).");
      }
    },
    [StudentId, CLASS_ID, SECTION_ID, SESSION_ID, getStudentTransportDetails]
  );

  const confirmRemoveFromTransport = useCallback(async () => {
    const blocked = mTransportFee.some(
      (fee) =>
        parseFloat(fee.amount_paid) > 0 || parseFloat(fee.amount_settled) > 0
    );
    if (blocked) {
      myToast.notify_error(
        "Cannot remove student from transport. Fee has been collected or settled for this student. Please contact administrator."
      );
      setShowRemoveConfirm(false);
      return;
    }
    if (!StudentId || !SESSION_ID) {
      myToast.notify_error(
        "Required information is missing. Please refresh the page and try again."
      );
      setShowRemoveConfirm(false);
      return;
    }

    setLoading(true);
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const STAFF_ID = SessionManager.getStaffDetails()?.staff_id;
      if (!STAFF_ID || !SCHOOL_CODE) {
        myToast.notify_error(
          "Session information is missing. Please login again."
        );
        setLoading(false);
        setShowRemoveConfirm(false);
        return;
      }

      const URL = `${API_BASE}DeleteTransportStudent.php?staff_id=${STAFF_ID}&student_id=${StudentId}&session_id=${SESSION_ID}&service_id=${SERVICE_ID}&school_code=${SCHOOL_CODE}`;
      const response = await Axios.get(URL);
      setLoading(false);

      if (response.data.api_status === "OK") {
        myToast.notify_success("Student removed from transport successfully!");
        window.location.replace("/AdminMain/TransportStudent");
      } else {
        myToast.notify_warning(
          response.data.api_remark ||
            "Failed to remove student from transport."
        );
      }
    } catch (error) {
      setLoading(false);
      console.error("Error removing student from transport:", error);
      myToast.notify_error("Failed to remove student from transport");
    }
    setShowRemoveConfirm(false);
  }, [mTransportFee, StudentId, SESSION_ID]);

  const handleEdit = useCallback((item) => {
    if (!canModifyMonth(item)) {
      myToast.notify_warning(
        "Edit is not allowed for this month. Transport fee has already been collected or settled. Please contact the administrator."
      );
      return;
    }
    if (!item) {
      myToast.notify_error("Service month data not found");
      return;
    }
    setEditingItem(item);
    setIsOpenEditServiceMonth(true);
  }, []);

  const handleDelete = useCallback((item) => {
    if (!canModifyMonth(item)) {
      myToast.notify_warning(
        "Delete is not allowed for this month. Transport fee has already been collected or settled. Please contact the administrator."
      );
      return;
    }
    setDeletingMonth(item);
    setShowDeleteMonthConfirm(true);
  }, []);

  const deleteServiceMonth = useCallback(
    async (month) => {
      setLoading(true);
      try {
        const SCHOOL_CODE = SessionManager.getSchoolCode();
        const STAFF_ID = SessionManager.getStaffDetails()?.staff_id;

        if (
          !STAFF_ID ||
          !SCHOOL_CODE ||
          !StudentId ||
          !SESSION_ID ||
          !CLASS_ID ||
          !SECTION_ID
        ) {
          myToast.notify_error(
            "Required information is missing. Please refresh the page and try again."
          );
          setLoading(false);
          return;
        }

        const URL = `${API_BASE}removeServiceMonth.php?staff_id=${STAFF_ID}&applicable_month=${month}&student_id=${StudentId}&class_id=${CLASS_ID}&section_id=${SECTION_ID}&session_id=${SESSION_ID}&service_id=${SERVICE_ID}&school_code=${SCHOOL_CODE}`;
        const response = await Axios.get(URL);
        setLoading(false);

        if (response.data.api_status === "OK") {
          myToast.notify_success(`Service month ${month} deleted successfully!`);
          getStudentTransportDetails();
        } else {
          myToast.notify_warning(
            response.data.api_remark || "Failed to delete service month."
          );
        }
      } catch (error) {
        setLoading(false);
        console.error("Error deleting service month:", error);
        myToast.notify_error("Failed to delete service month");
      }
    },
    [StudentId, SESSION_ID, CLASS_ID, SECTION_ID, getStudentTransportDetails]
  );

  const confirmDeleteMonth = useCallback(() => {
    if (deletingMonth) {
      const rawMonth =
        deletingMonth.applicable_month || deletingMonth.service_month;
      deleteServiceMonth(rawMonth);
    }
    setShowDeleteMonthConfirm(false);
    setDeletingMonth(null);
  }, [deletingMonth, deleteServiceMonth]);

  const saveServiceMonthEdit = useCallback(
    async (updatedItem) => {
      setLoading(true);
      try {
        const SCHOOL_CODE = SessionManager.getSchoolCode();
        const STAFF_ID = SessionManager.getStaffDetails()?.staff_id;
        if (!SCHOOL_CODE || !STAFF_ID) {
          myToast.notify_error(
            "Session information is missing. Please login again."
          );
          setLoading(false);
          return;
        }

        const serviceMonth =
          updatedItem.service_month || updatedItem.applicable_month || "";
        if (!serviceMonth) {
          myToast.notify_error(
            "Service month is missing. Please refresh the page and try again."
          );
          setLoading(false);
          return;
        }

        const serviceFee =
          updatedItem.service_fee !== undefined &&
          updatedItem.service_fee !== null &&
          updatedItem.service_fee !== ""
            ? updatedItem.service_fee
            : updatedItem.net_service_fee;

        const netServiceFeeOld =
          updatedItem.net_service_fee_old !== undefined &&
          updatedItem.net_service_fee_old !== null &&
          updatedItem.net_service_fee_old !== ""
            ? updatedItem.net_service_fee_old
            : updatedItem.net_service_fee;

        const netServiceFeeNew =
          updatedItem.net_service_fee !== undefined &&
          updatedItem.net_service_fee !== null &&
          updatedItem.net_service_fee !== ""
            ? updatedItem.net_service_fee
            : 0;

        const params = new URLSearchParams({
          staff_id: STAFF_ID,
          service_month: serviceMonth,
          student_id: StudentId,
          service_fee: serviceFee,
          net_service_fee_old: netServiceFeeOld,
          net_service_fee_new: netServiceFeeNew,
          session_id: SESSION_ID,
          service_id: SERVICE_ID,
          school_code: SCHOOL_CODE,
        });

        const response = await Axios.get(
          `${API_BASE}updateTransportServiceMonthFee.php?${params.toString()}`
        );
        setLoading(false);

        if (response.data.api_status === "OK") {
          myToast.notify_success("Service month updated successfully!");
          getStudentTransportDetails();
        } else {
          myToast.notify_warning(
            response.data.api_remark || "Failed to update service month."
          );
        }
      } catch (error) {
        setLoading(false);
        console.error("Error updating service month:", error);
        myToast.notify_error("Failed to update service month");
      }
    },
    [StudentId, SESSION_ID, getStudentTransportDetails]
  );

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page sr-page tsd-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>
                {" "}
                Transport Student Details
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBus />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Transport Student Details</h1>
              <p className="sm-hero__sub">
                View route, pickup, and monthly transport fees
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/TransportStudent" className="tsd-hero-link">
              <FaArrowLeft aria-hidden /> Students
            </Link>
          </div>
        </header>

        {loading && !student ? (
          <div className="tsd-loading">
            <MyLoader />
          </div>
        ) : (
          <div className="sr-body tsd-body">
            {loading ? (
              <div className="tsd-loading tsd-loading--inline">
                <MyLoader />
              </div>
            ) : null}

            <section className="sr-section tsd-section tsd-section--student">
              <div className="sr-section__head">
                <div className="sr-section__icon" aria-hidden>
                  <FaUser />
                </div>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Student</h2>
                  <p className="sr-section__sub">Profile and class details</p>
                </div>
              </div>
              <div className="sr-section__body">
                {student ? (
                  <div className="tsd-student">
                    <div className="tsd-student__avatar" aria-hidden>
                      {student.student_img_path ? (
                        <img
                          src={student.student_img_path}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <FaUser />
                      )}
                    </div>
                    <div className="tsd-student__info min-w-0">
                      <h3 className="tsd-student__name">
                        {student.stu_name || "Name"}
                        {student.father_name
                          ? ` S/o ${student.father_name}`
                          : ""}
                      </h3>
                      <div className="tsd-student__pills">
                        <span className="tsd-pill">
                          Roll: {classInfo?.student_roll_no || "—"}
                        </span>
                        <span className="tsd-pill">Class: {classLabel}</span>
                        {student.student_enrollment_no ? (
                          <span className="tsd-pill">
                            Enroll: {student.student_enrollment_no}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="tsd-empty">No student data found.</div>
                )}
              </div>
            </section>

            <section className="sr-section tsd-section tsd-section--transport">
              <div className="sr-section__head">
                <div className="sr-section__icon" aria-hidden>
                  <FaBus />
                </div>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Transport details</h2>
                  <p className="sr-section__sub">
                    Vehicle, route, and pickup information
                  </p>
                </div>
              </div>
              <div className="sr-section__body">
                {transport ? (
                  <div className="tsd-meta-grid">
                    <div className="tsd-meta">
                      <span className="tsd-meta__label">
                        <FaBus aria-hidden /> Vehicle
                      </span>
                      <span className="tsd-meta__value">
                        {transport.vehicle_name || "—"}
                      </span>
                      <span className="tsd-meta__sub">
                        {transport.vehicle_type || "—"} ·{" "}
                        {transport.vehicle_number || "—"}
                      </span>
                    </div>
                    <div className="tsd-meta">
                      <span className="tsd-meta__label">
                        <FaRoute aria-hidden /> Route
                      </span>
                      <span className="tsd-meta__value">
                        {transport.route_name || "—"}
                      </span>
                    </div>
                    <div className="tsd-meta">
                      <span className="tsd-meta__label">
                        <FaMapMarkerAlt aria-hidden /> Pickup point
                      </span>
                      <span className="tsd-meta__value">
                        {transport.pickup_point_name || "—"}
                      </span>
                    </div>
                    <div className="tsd-meta">
                      <span className="tsd-meta__label">
                        <FaClock aria-hidden /> Pickup time
                      </span>
                      <span className="tsd-meta__value">
                        {transport.pickup_time || "—"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="tsd-empty">No transport details available.</div>
                )}
              </div>
            </section>

            <section className="sr-section tsd-section tsd-section--months">
              <div className="sr-section__head">
                <div className="sr-section__icon" aria-hidden>
                  <FaCalendarAlt />
                </div>
                <div className="min-w-0">
                  <h2 className="sr-section__title">Service months</h2>
                  <p className="sr-section__sub">
                    {mTransportFee.length
                      ? `${mTransportFee.length} month${
                          mTransportFee.length === 1 ? "" : "s"
                        } assigned`
                      : "Monthly fee payment details"}
                  </p>
                </div>
              </div>
              <div className="sr-section__body">
                {mTransportFee.length === 0 ? (
                  <div className="tsd-empty">
                    No service months yet. Add a month to continue.
                  </div>
                ) : (
                  <div className="tsd-months">
                    {mTransportFee.map((item, index) => {
                      const editable = canModifyMonth(item);
                      const key =
                        item.applicable_month ||
                        item.service_month ||
                        `month-${index}`;
                      return (
                        <article key={key} className="tsd-month">
                          <div className="tsd-month__title">
                            <span className="tsd-month__icon" aria-hidden>
                              <FaCalendarAlt />
                            </span>
                            <span>{getApplicableMonth(item)}</span>
                          </div>
                          <div className="tsd-month__stats">
                            <div className="tsd-stat">
                              <span className="tsd-stat__label">Total</span>
                              <span className="tsd-stat__value">
                                ₹ {money(item.net_service_fee)}
                              </span>
                            </div>
                            <div className="tsd-stat tsd-stat--paid">
                              <span className="tsd-stat__label">Paid</span>
                              <span className="tsd-stat__value">
                                ₹ {money(item.amount_paid)}
                              </span>
                            </div>
                            <div className="tsd-stat tsd-stat--due">
                              <span className="tsd-stat__label">Due</span>
                              <span className="tsd-stat__value">
                                ₹ {money(item.outstanding)}
                              </span>
                            </div>
                            <div className="tsd-stat tsd-stat--settled">
                              <span className="tsd-stat__label">Settled</span>
                              <span className="tsd-stat__value">
                                ₹ {money(item.amount_settled || 0)}
                              </span>
                            </div>
                          </div>
                          <div className="tsd-month__actions">
                            <button
                              type="button"
                              className={`tsd-icon-btn tsd-icon-btn--edit${
                                editable ? "" : " is-disabled"
                              }`}
                              onClick={() => handleEdit(item)}
                              title={
                                editable
                                  ? "Edit service month"
                                  : "Edit disabled: fee collected or settled"
                              }
                            >
                              <FaEdit aria-hidden />
                            </button>
                            <button
                              type="button"
                              className={`tsd-icon-btn tsd-icon-btn--delete${
                                editable ? "" : " is-disabled"
                              }`}
                              onClick={() => handleDelete(item)}
                              title={
                                editable
                                  ? "Delete service month"
                                  : "Delete disabled: fee collected or settled"
                              }
                            >
                              <FaTrashAlt aria-hidden />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <div className="tsd-actions">
              <button
                type="button"
                className="sr-btn sr-btn--primary tsd-btn-add"
                onClick={AddServiceMonth}
              >
                <FaPlus aria-hidden /> Add Service Month
              </button>
              <button
                type="button"
                className="sr-btn tsd-btn-remove"
                onClick={() => setShowRemoveConfirm(true)}
                disabled={hasCollectedFee}
                title={
                  hasCollectedFee
                    ? "Cannot remove: fee collected or settled"
                    : "Remove student from transport"
                }
              >
                <FaUserTimes aria-hidden /> Remove From Transport
              </button>
            </div>
          </div>
        )}
      </div>

      {isOpenAddServiceMonth ? (
        <MdlAddServiceMonth
          existingMonths={mTransportFee
            .map((item) => item.service_month || item.applicable_month)
            .filter(Boolean)}
          onSave={handleAddServiceMonths}
          actionClose={handleCloseAddServiceMonth}
        />
      ) : null}

      {isOpenEditServiceMonth && editingItem ? (
        <MdlEditServiceMonth
          item={editingItem}
          actionClose={handleCloseEditServiceMonth}
          actionSave={saveServiceMonthEdit}
        />
      ) : null}

      {showRemoveConfirm ? (
        <div className="tsd-modal" role="dialog" aria-modal="true">
          <div className="tsd-modal__card">
            <div className="tsd-modal__head">
              <div className="tsd-modal__icon tsd-modal__icon--danger" aria-hidden>
                <FaExclamationTriangle />
              </div>
              <div>
                <h3 className="tsd-modal__title">Remove from Transport</h3>
                <p className="tsd-modal__sub">This action cannot be undone</p>
              </div>
            </div>
            <p className="tsd-modal__body">
              Remove <strong>{student?.stu_name || "this student"}</strong> from
              transport? All transport records for this student will be deleted.
              Allowed only if no fees are collected or settled.
            </p>
            <div className="tsd-modal__actions">
              <button
                type="button"
                className="sr-btn sr-btn--ghost"
                onClick={() => setShowRemoveConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sr-btn tsd-btn-danger"
                onClick={confirmRemoveFromTransport}
                disabled={loading}
              >
                {loading ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showDeleteMonthConfirm ? (
        <div className="tsd-modal" role="dialog" aria-modal="true">
          <div className="tsd-modal__card">
            <div className="tsd-modal__head">
              <div className="tsd-modal__icon tsd-modal__icon--warn" aria-hidden>
                <FaCalendarAlt />
              </div>
              <div>
                <h3 className="tsd-modal__title">Delete Service Month</h3>
                <p className="tsd-modal__sub">This action cannot be undone</p>
              </div>
            </div>
            <p className="tsd-modal__body">
              Delete service month{" "}
              <strong>
                {deletingMonth ? getApplicableMonth(deletingMonth) : ""}
              </strong>
              ? The transport fee record for this month will be removed.
            </p>
            <div className="tsd-modal__actions">
              <button
                type="button"
                className="sr-btn sr-btn--ghost"
                onClick={() => {
                  setShowDeleteMonthConfirm(false);
                  setDeletingMonth(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sr-btn tsd-btn-warn"
                onClick={confirmDeleteMonth}
                disabled={loading}
              >
                {loading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TransportStudentDetails;
